import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { claseAvance, clasePago, iconoAvance, iconoPago } from '../ficha-tanda/ficha-tanda.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { RolGuia, rolDesdeUrl } from 'src/app/utils/liquidacion-guia';
import { guardarArchivo } from 'src/app/utils/descargar';
import { filasDeCortes, firmaDeCortes, lotesEnVivo, puedeCerrarConCuadre, renglonesDelCuadre, TEXTO_ASUMO_CUADRE, textoCadena, textoSinCuadre } from 'src/app/utils/liquidacion-cuadre';
import { etiquetaSinFirmante as etiquetaSF, rolesParams as rolesP, textoSinFirmante as textoSF } from 'src/app/utils/liquidacion-firmantes';
import { agruparPorCorte, claveDe, debeSondear, etiquetaCorte, etiquetaCortes, horaDelCorte, puedeConfirmarPrevia, sinLatido, textoAvance, textoVentanaPrevia, ventanaDelCorte } from 'src/app/utils/liquidacion-cortes';
import { incluyeLegacy as incluyeLegacyDe, textoVersiones as textoVersionesDe } from './versiones-del-renglon';

/**
 * LIQUIDACIÓN · EL DÍA Y EL CIERRE (4.6a; maqueta aprobada, vistas 0 a 3 y 16).
 *
 *   El día   cuánto hay que pagar hoy y a quién: tira de cifras y tabla por comercio con sus observaciones.
 *   Auditar  las tres hojas del Excel en pantalla (Resumen · Detalle · Cuadre) y la descarga.
 *   Cierre   el tesorero marca comercios; lo bloqueado no se marca y dice por qué (lo resuelve el núcleo);
 *            la banda sale EN VIVO del monto marcado; envía la tanda; decide incluir o pasar a mañana; cierra el día.
 *
 * Decisiones de Mario (08-oct) que la maqueta vieja no tenía: la cuarentena no bloquea (solo avisa) y los
 * atrasados van en la tanda que el tesorero elija. Sin diálogos: todo se confirma en la página.
 */
@Component({
  selector: 'app-liquidacion-dia',
  templateUrl: './liquidacion-dia.component.html'
})
export class LiquidacionDiaComponent implements OnInit, OnDestroy {
  dia = this.hoy();
  pestana: 'dia' | 'cierre' | 'aprobar' | 'lotes' | 'resolver' | 'guia' = 'dia';
  /** GUI-02: el `?rol=` de la URL (válido) manda sobre el que se deduce de los permisos. */
  rolUrl: RolGuia | null = null;
  /** LQV-09: el calendario del mes es opcional (Mario, 24-oct: «quita espacio»); oculto salvo que se haya dejado abierto. */
  verCalendario = this.leerVerCalendario();
  cobranzaAbierta: string | null = null;   // 6.5: el detalle de cobranza abierto en la tabla del cierre
  cargando = true;
  error = '';
  aviso = '';
  cierre: any = null;

  yo: { id: string; roles: string[] } = { id: '', roles: [] };
  bandaVigente: any = null;

  // auditar
  hoja: 'Resumen' | 'Detalle' | 'Cuadre' = 'Resumen';
  auditoria: any = null;
  verAuditoria = false;

  // cierre
  marcadas = new Set<string>();
  enviando = false;
  decidiendo: string | null = null;
  motivoManana = '';
  pasando: string | null = null;
  ocupado = false;

  readonly ROL: any = { TES: 'Tesorero', GER: 'Gerente de finanzas', DIR: 'Director' };

  constructor(private ruta: ActivatedRoute, private router: Router, private liq: LiquidacionService, public _auth: AuthService, private zona: NgZone) { }

  // LQV-03 (en vivo, 24-oct): tabla (≥1100 px) y tarjeta (<1100 px) viven en el DOM a la vez; montar el panel de abonos
  // en ambas pedía el detalle dos veces. Solo se monta el de la vista que se ve.
  private mq = window.matchMedia('(min-width: 1100px)');
  anchoTabla = this.mq.matches;
  private alCambiarAncho = (e: MediaQueryListEvent) => this.zona.run(() => { this.anchoTabla = e.matches; });

  ngOnInit(): void {
    this.mq.addEventListener('change', this.alCambiarAncho);
    this.sub = this.ruta.queryParamMap.subscribe((q) => {
      const d = q.get('dia');
      this.dia = d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : this.hoy();
      const v = q.get('vista');
      this.pestana = (['cierre', 'aprobar', 'lotes', 'resolver', 'guia'].includes(v || '') ? v : 'dia') as any;
      this.rolUrl = rolDesdeUrl(q.get('rol'));
      // Lotes y «Por resolver» (5.5) cargan lo suyo en su componente.
      // RSM-11: Por aprobar la ven todos; sin permiso de aprobar, en lectura (las tandas por firmar del cierre).
      if (this.pestana === 'aprobar') { if (this.puedeAprobar) this.cargarBandeja(); else this.cargar(); }
      // El Resumen (RSM-09), Lotes y «Por resolver» cargan lo suyo en su componente.
      else if (this.pestana === 'dia' || this.pestana === 'lotes' || this.pestana === 'resolver' || this.pestana === 'guia') { this.cargando = false; this.error = ''; }
      else this.cargar();
    });
    // LPR-02 (Mario, 14-oct): la pestaña «Por resolver» dice cuántos pagos esperan decisión.
    this.liq.noProcesados().then((r) => { this.porResolver = ((r && r.pagos) || []).length; }).catch(() => { });
    this.liq.yo().then((y) => { this.yo = y; }).catch(() => { });
    this.liq.bandas().then((b) => { this.bandaVigente = b && b.vigente; }).catch(() => { });
  }

  ngOnDestroy(): void { this.pararSondeo(); this.sub?.unsubscribe(); this.mq.removeEventListener('change', this.alCambiarAncho); }

  // ══ Cortes del día (13.4; Mario, 23-oct): una liquidación por corte dentro del cierre ══
  readonly claveDe = claveDe;
  readonly etiquetaCorte = etiquetaCorte;
  /** El corte (o los cortes) de una tanda, por las liquidaciones del cierre que lleva. */
  corteDeTanda = (t: any): string => etiquetaCortes(this.comerciosDe(t).map((l) => l.corte || 1));
  readonly horaDelCorte = horaDelCorte;
  readonly ventanaDelCorte = ventanaDelCorte;
  readonly textoVentanaPrevia = textoVentanaPrevia;
  readonly textoAvance = textoAvance;
  sinLatido = (c: any): boolean => sinLatido(c);
  cortes: any[] = [];
  vistaPrevia: any = null;          // la vista previa del corte siguiente (abierta = no nula)
  cargandoPrevia = false;
  haciendoCorte = false;
  reintentando: number | null = null;
  errorCorte = '';
  private temporizador: any = null;
  private sub: Subscription | null = null;
  private diaSondeo = '';

  /** Las liquidaciones agrupadas por corte, con su encabezado «Corte N · HH:MM». */
  // Se arma una sola vez por cada lista de liquidaciones y de cortes: un arreglo nuevo en cada ciclo rehace las filas sin fin.
  private gruposCache: { liq: any[] | null; cortes: any[] | null; grupos: { n: number; titulo: string; liquidaciones: any[] }[] } = { liq: null, cortes: null, grupos: [] };
  get grupos(): { n: number; titulo: string; liquidaciones: any[] }[] {
    const k = this.gruposCache;
    if (k.liq !== this.liquidaciones || k.cortes !== this.cortes) this.gruposCache = { liq: this.liquidaciones, cortes: this.cortes, grupos: agruparPorCorte(this.liquidaciones, this.cortes) };
    return this.gruposCache.grupos;
  }
  porGrupo = (_: number, g: { n: number }) => g.n;
  porClave = (_: number, l: any) => claveDe(l);
  /** Los encabezados «Corte N · HH:MM» salen cuando el día tiene más de un corte (con uno solo la vista es la de siempre). */
  get conEncabezados(): boolean { return this.cortes.length > 1 || this.grupos.length > 1; }
  get puedeCerrarElDia(): boolean { return !(this.previa?.requiere_responsable && !this.asumo) && puedeCerrarConCuadre(this.previa?.cuadre, this.asumoCuadre); }
  get puedeConfirmar(): boolean { return puedeConfirmarPrevia(this.vistaPrevia); }
  get cortesEnCurso(): any[] { return this.cortes.filter((c) => c.estado === 'EN_CURSO'); }
  get hayCorteFallido(): boolean { return this.cortes.some((c) => c.estado === 'FALLIDO'); }
  /** «Hacer corte» solo con el día abierto, sin cortes en curso ni caídos y con permiso de preparar. */
  get puedeHacerCorte(): boolean { return this.puedePreparar && !!this.cierre?.guardado && !this.cierre?.cerrado?.en && !this.cortesEnCurso.length && !this.hayCorteFallido && !this.antesDelInicio; }
  montoDelCorte = (c: any): number | null => { const r = c.resumen; return r && r.neto != null ? Number(r.neto) : null; };
  estadoDelCorte = (c: any): { texto: string; clase: string } => ({
    EN_CURSO: { texto: 'En curso', clase: 'kb-badge-neutro' }, LISTO: { texto: 'Listo', clase: 'kb-badge-exito' },
    FALLIDO: { texto: 'Se detuvo', clase: 'kb-badge-peligro' }, VACIO: { texto: 'Sin movimientos', clase: 'kb-badge-neutro' },
  } as any)[c.estado] || { texto: c.estado, clase: 'kb-badge-neutro' };

  // ══ Cuadre de caja (13.3; Mario, 24-oct: «Compacto, por corte al abrir») ══
  cuadre: any = null;
  cuadreAbierto = false;
  bajandoCuadre = false;
  private firmaCuadre = '';
  readonly TEXTO_ASUMO_CUADRE = TEXTO_ASUMO_CUADRE;
  readonly textoSinCuadre = textoSinCuadre;
  /** Se arman una sola vez por respuesta: un arreglo nuevo en cada ciclo rehace las filas sin fin (lo que colgó el navegador en la 13.4). */
  private cuadreCache: { src: any; renglones: any[]; filas: any[]; lotes: any[]; cadena: string } = { src: null, renglones: [], filas: [], lotes: [], cadena: '' };
  private cache = (): any => {
    if (this.cuadreCache.src !== this.cuadre) {
      const c = this.cuadre;
      this.cuadreCache = { src: c, renglones: renglonesDelCuadre(c), filas: filasDeCortes(c && c.cortes), lotes: lotesEnVivo(c && c.lotes_en_vivo), cadena: textoCadena(c && c.cadena) };
    }
    return this.cuadreCache;
  };
  get renglonesCuadre(): any[] { return this.cache().renglones; }
  get filasCuadre(): any[] { return this.cache().filas; }
  get lotesCuadre(): any[] { return this.cache().lotes; }
  get textoCadenaCuadre(): string { return this.cache().cadena; }
  trackPorId = (_: number, x: any): any => x.id || x.clave || x.n;
  /** Se pide el cuadre al abrir el cierre y cada vez que cambian los cortes (nace uno o cambia de estado); el sondeo es el de los cortes. */
  private cargarCuadre = async (forzar: boolean = false): Promise<void> => {
    const dia = this.dia, firma = firmaDeCortes(this.cortes);
    if (!forzar && firma === this.firmaCuadre) return;
    this.firmaCuadre = firma;
    try { const r = await this.liq.verCuadre(dia); if (dia === this.dia) this.cuadre = r || null; }
    catch { if (dia === this.dia) this.cuadre = null; }
  };
  bajarCuadre = async (): Promise<void> => {
    this.bajandoCuadre = true; this.error = '';
    try { guardarArchivo(await this.liq.cuadreExcel(this.dia), `cuadre-de-caja-${this.dia.replace(/-/g, '')}.xlsx`); }
    catch (e: any) {
      let msg = ''; try { msg = JSON.parse(await e?.error?.text()).msg; } catch (x) { }
      this.error = msg || 'No se pudo descargar el Excel del cuadre.';
    } finally { this.bajandoCuadre = false; }
  };

  cargarCortes = async (forzarCuadre: boolean = false): Promise<void> => {
    const dia = this.dia;
    try {
      const r = await this.liq.verCortes(dia);
      if (dia !== this.dia) return;
      const antes = this.cortes.filter((c) => c.estado === 'EN_CURSO').map((c) => c.n);
      this.cortes = (r && r.cortes) || [];
      // Un corte que estaba en curso y ya terminó: se recarga la vista del cierre para ver sus liquidaciones.
      const terminoAlguno = antes.some((n) => (this.cortes.find((c) => c.n === n) || {}).estado === 'LISTO');
      if (debeSondear(this.cortes)) this.empezarSondeo(); else this.pararSondeo();
      await this.cargarCuadre(forzarCuadre);
      if (terminoAlguno) await this.cargar();
    } catch { this.cortes = []; this.cuadre = null; this.pararSondeo(); }
  };
  /** Mientras haya un corte EN_CURSO se consulta su avance cada 3 s; se corta al terminar y al salir de la pantalla. */
  private empezarSondeo = (): void => {
    if (this.temporizador) return;
    this.diaSondeo = this.dia;
    this.temporizador = setInterval(() => { if (this.diaSondeo !== this.dia) { this.pararSondeo(); return; } this.cargarCortes(); }, 3000);
  };
  private pararSondeo = (): void => { if (this.temporizador) { clearInterval(this.temporizador); this.temporizador = null; } };

  abrirVistaPrevia = async (): Promise<void> => {
    this.cargandoPrevia = true; this.errorCorte = ''; this.vistaPrevia = null;
    try { this.vistaPrevia = await this.liq.vistaPreviaCorte(this.dia); }
    catch (e: any) { this.errorCorte = e?.error?.msg || 'No se pudo preparar la vista previa del corte.'; }
    finally { this.cargandoPrevia = false; }
  };
  cerrarVistaPrevia = (): void => { this.vistaPrevia = null; };
  confirmarCorte = async (): Promise<void> => {
    if (!this.puedeConfirmar || this.haciendoCorte) return;
    this.haciendoCorte = true; this.errorCorte = '';
    try { await this.liq.hacerCorte(this.dia); this.vistaPrevia = null; await this.cargarCortes(); }
    catch (e: any) { this.errorCorte = e?.error?.msg || 'No se pudo hacer el corte.'; this.vistaPrevia = null; await this.cargarCortes(); }
    finally { this.haciendoCorte = false; }
  };
  reintentarCorte = async (c: any): Promise<void> => {
    if (this.reintentando) return;
    this.reintentando = c.n; this.errorCorte = '';
    try { await this.liq.reintentarCorte(this.dia, c.n); await this.cargarCortes(); }
    catch (e: any) { this.errorCorte = e?.error?.msg || 'No se pudo reintentar el corte.'; await this.cargarCortes(); }
    finally { this.reintentando = null; }
  };

  get puedePreparar(): boolean { return this._auth.hasPermission('liquidacion.preparar'); }
  get puedeAprobar(): boolean { return this._auth.hasPermission('liquidacion.aprobar'); }
  /** CIE-12: cierran el día el tesorero y, como respaldo, la gerente de finanzas. */
  get puedeCerrar(): boolean { return this._auth.hasPermission('liquidacion.cerrar_dia'); }
  get puedeResolver(): boolean { return this._auth.hasPermission('liquidacion.preparar') || this._auth.hasPermission('liquidacion.salud.liberar'); }

  cargar = async (): Promise<void> => {
    this.cargando = true; this.error = ''; this.aviso = ''; this.marcadas.clear(); this.auditoria = null; this.verAuditoria = false;
    try {
      this.cierre = await this.liq.vistaCierre(this.dia);
      this.aviso = this.cierre && this.cierre.aviso || '';
      if (this.cierre && this.cierre.guardado) await this.cargarCortes(true); else { this.cortes = []; this.cuadre = null; this.cuadreAbierto = false; this.pararSondeo(); }
    } catch (e: any) {
      this.cierre = null;
      this.error = e?.error?.msg || 'No se pudo leer el cierre.';
    } finally { this.cargando = false; }
  };

  private leerVerCalendario(): boolean { try { return localStorage.getItem('liquidacion.calendario-mes.ver') === '1'; } catch { return false; } }
  alternarCalendario = (): void => {
    this.verCalendario = !this.verCalendario;
    try { localStorage.setItem('liquidacion.calendario-mes.ver', this.verCalendario ? '1' : '0'); } catch { /* sin almacenamiento: no se recuerda */ }
  };

  irA = (dia: string, vista: string = this.pestana): void => {
    this.router.navigate([], { queryParams: { dia, vista }, queryParamsHandling: 'merge' });
  };
  moverDia = (n: number): void => { const d = new Date(`${this.dia}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); this.irA(d.toISOString().slice(0, 10)); };
  verPestana = (p: 'dia' | 'cierre' | 'aprobar' | 'lotes' | 'resolver' | 'guia'): void => {
    this.router.navigate([], { queryParams: { dia: this.dia, vista: p, lote: null }, queryParamsHandling: 'merge' });
  };

  /** LQV-02: el texto del botón que abre los abonos de un comercio de cobranza («Ver 120 abonos»); sin la cantidad, «Ver abonos». */
  textoVerAbonos = (n: any): string => (Number(n) > 0 ? `Ver ${Number(n)} ${Number(n) === 1 ? 'abono' : 'abonos'}` : 'Ver abonos');

  // ── cifras ──
  /** «2 comercios» o, con varios cortes, «2 comercios · 4 liquidaciones» (cada corte trae su liquidación por comercio). */
  get conteoComercios(): string {
    const n = new Set(this.liquidaciones.map((l) => String(l.code))).size; const m = this.liquidaciones.length;
    return `${n} ${n === 1 ? 'comercio' : 'comercios'}` + (m > n ? ` · ${m} liquidaciones` : '');
  }
  get liquidaciones(): any[] { return (this.cierre && this.cierre.liquidaciones) || []; }
  /** Antes de LIQ_INICIO el día no tiene cierre: lo que vencía lo pagó el proceso anterior (recorrido en MT, 08-oct). */
  get antesDelInicio(): boolean { return !!(this.cierre && this.cierre.inicio_liquidacion && this.dia < this.cierre.inicio_liquidacion); }
  textoVersiones = textoVersionesDe;
  incluyeLegacy = incluyeLegacyDe;
  suma = (ls: any[], k = 'neto'): number => Math.round(ls.reduce((s, l) => s + (Number(l.totales && l.totales[k]) || 0), 0) * 100) / 100;
  get hoySePaga(): number { return this.suma(this.liquidaciones.filter((l) => l.totales && l.totales.pagable && l.state !== 'EXCLUDED')); }
  get aprobado(): number { return this.suma(this.liquidaciones.filter((l) => l.state === 'APPROVED' || l.state === 'PAID')); }
  get atrasado(): number { return this.suma(this.liquidaciones.filter((l) => l.atrasada)); }
  get ventas(): number { return this.liquidaciones.reduce((s, l) => s + (l.modelo === 'COBRANZA' ? ((l.cobranza && l.cobranza.ventas) || 0) : (l.partidas || []).length), 0); }

  // ── vista Cierre (Mario, 12-oct; maqueta vista Cierre; CIE-14 a 17) ──
  /** Por decidir: lo que todavía no está aprobado ni pasó a mañana ni se arrastra (también lo que espera firma). */
  get porDecidir(): number { return this.suma(this.liquidaciones.filter((l) => l.state === 'DRAFT' && l.totales && l.totales.pagable)); }
  get tandasPorFirmar(): number { return ((this.cierre && this.cierre.tandas) || []).filter((t: any) => ['POR_APROBAR', 'FIRMA_1_DE_2'].includes(t.state)).length; }
  /** Todo monto en USD con su Bs a la tasa BCV del día del cierre (null si todavía no hay tasa). */
  bs = (usd: number): number | null => (this.cierre && this.cierre.tasa ? Math.round((Number(usd) || 0) * this.cierre.tasa * 100) / 100 : null);
  hora = (en: any): string => (en ? new Date(new Date(en).getTime() - 4 * 3600e3).toISOString().slice(11, 16) : '—');
  /** La tanda viva en la que está un comercio (por firmar o con una de dos firmas), si la hay. */
  tandaViva = (l: any): any => ((this.cierre && this.cierre.tandas) || []).find((t: any) => ['POR_APROBAR', 'FIRMA_1_DE_2'].includes(t.state) && (t.liquidaciones || []).map(String).includes(claveDe(l)));
  /** CIE-15: el estado de cada comercio en el cierre, de un vistazo. */
  // ══ seguir el pago (SEG-01) y la ficha rápida de la tanda (FTA-01; Mario, 14-oct) ══
  readonly claseAvance = claseAvance;
  readonly iconoAvance = iconoAvance;
  readonly iconoPago = iconoPago;
  porResolver = 0;
  fichaT: any = null;
  cargandoT = false;
  errorT = '';
  abrirTanda = async (t: any): Promise<void> => {
    this.fichaT = { n: t.n, comercios: [], lotes: [] }; this.errorT = ''; this.cargandoT = true;
    try {
      this.fichaT = await this.liq.fichaTanda(this.dia, t.n);
      // 13.4: cada comercio de la ficha dice su corte (el de su liquidación en el cierre) si el núcleo no lo manda.
      for (const c of this.fichaT.comercios || []) if (!c.corte) c.corte = (this.liquidaciones.find((l) => l.liquidacion_code && l.liquidacion_code === c.liquidacion) || {}).corte;
    } catch (e: any) { this.errorT = e?.error?.msg || 'No se pudo leer la tanda.'; } finally { this.cargandoT = false; }
  };
  cerrarTanda = (): void => { this.fichaT = null; };
  verLoteDeTanda = (code: string): void => { this.fichaT = null; this.router.navigate([], { queryParams: { dia: this.dia, vista: 'lotes', lote: code }, queryParamsHandling: 'merge' }); };

  estadoEnCierre = (l: any): { texto: string; clase: string } => {
    // SEG-01: aprobado, el estado sigue su pago (en lote, en el banco, no procesado, reintento, pagado), en vivo.
    if (l.pago?.texto && (l.state === 'APPROVED' || l.state === 'PAID')) return { texto: l.pago.texto, clase: clasePago(l.pago) };
    if (l.state === 'APPROVED' || l.state === 'PAID') return { texto: `Aprobado · tanda ${l.tanda}`, clase: 'kb-badge !border-transparent !bg-foreground !text-background' };
    if (l.state === 'EXCLUDED') return { texto: l.arrastre ? 'Se arrastra · neto ≤ 0' : 'Pasó a mañana', clase: 'kb-badge kb-badge-alerta' };
    const t = this.tandaViva(l);
    if (t) return { texto: `En tanda ${t.n}${t.state === 'FIRMA_1_DE_2' ? ' · 1 de 2 firmas' : ' · por firmar'}`, clase: 'kb-badge kb-badge-neutro !border-dashed' };
    if (l.totales && !l.totales.pagable) return { texto: 'Se arrastra · neto ≤ 0', clase: 'kb-badge kb-badge-neutro' };
    return l.puede_ir_en_tanda ? { texto: 'Sin tanda · lista para marcar', clase: 'kb-badge kb-badge-exito' } : { texto: 'Sin tanda · por revisar', clase: 'kb-badge kb-badge-alerta' };
  };
  /** Lo que el tesorero todavía puede decidir de un comercio (incluir o pasar a mañana). */
  decidible = (l: any): boolean => this.puedePreparar && l.state === 'DRAFT' && !this.tandaViva(l) && !this.cierre?.cerrado?.en;
  /** Los comercios de una tanda, con su neto (para las tarjetas de tandas). */
  comerciosDe = (t: any): any[] => this.liquidaciones.filter((l) => (t.liquidaciones || []).map(String).includes(claveDe(l)));

  estado = (l: any): { texto: string; clase: string } => ({
    DRAFT: { texto: 'Por liquidar', clase: 'kb-badge-neutro' }, APPROVED: { texto: 'Aprobada', clase: 'kb-badge-exito' },
    PAID: { texto: 'Pagada', clase: 'kb-badge-exito' }, EXCLUDED: { texto: 'Pasó a mañana', clase: 'kb-badge-alerta' },
  } as any)[l.state] || { texto: l.state, clase: 'kb-badge-neutro' };

  // ── auditar ──
  abrirAuditoria = async (): Promise<void> => {
    this.verAuditoria = !this.verAuditoria;
    if (this.verAuditoria && !this.auditoria) {
      try { this.auditoria = await this.liq.auditoriaJson(this.dia); } catch (e: any) { this.error = e?.error?.msg || 'No se pudo armar la auditoría.'; }
    }
  };

  descargar = async (seleccion: string[] = []): Promise<void> => {
    try {
      const blob = await this.liq.descargarAuditoria(this.dia, seleccion);
      guardarArchivo(blob, `auditoria-cierre-${this.dia.replace(/-/g, '')}${seleccion.length ? '-marcado' : ''}.xlsx`);
    } catch (e: any) { this.error = 'No se pudo descargar el Excel.'; }
  };

  // ── cierre ──
  abrir = async (): Promise<void> => {
    this.ocupado = true; this.error = '';
    try { await this.liq.abrirCierre(this.dia); await this.cargar(); } catch (e: any) { this.error = e?.error?.msg || 'No se pudo abrir el cierre.'; }
    finally { this.ocupado = false; }
  };

  marcar = (l: any): void => { if (!l.puede_ir_en_tanda) return; this.marcadas.has(claveDe(l)) ? this.marcadas.delete(claveDe(l)) : this.marcadas.add(claveDe(l)); };
  get seleccion(): string[] { return [...this.marcadas]; }
  get hayParaMarcar(): boolean { return this.liquidaciones.some((l) => l.puede_ir_en_tanda); }
  get montoMarcado(): number { return this.suma(this.liquidaciones.filter((l) => this.marcadas.has(claveDe(l)))); }

  /** BAN-01: la banda de lo marcado, en vivo (las mismas reglas que el núcleo). */
  bandaDeMonto = (monto: number): any => {
    if (!this.bandaVigente) return null;
    const i = this.bandaVigente.bandas.findIndex((b: any) => b.hasta === null || monto <= b.hasta);
    return { n: i + 1, ...this.bandaVigente.bandas[i] };
  };
  get banda(): any { return this.marcadas.size ? this.bandaDeMonto(this.montoMarcado) : null; }
  get dentroDeMiBanda(): boolean { return !!this.banda && this.banda.firmas[0] === 'TES' && this.yo.roles.includes('TES'); }
  firmantes = (b: any): string => (b?.firmas || []).map((r: string) => this.ROL[r] || r).join(' y luego ');
  get siguienteTanda(): number { return ((this.cierre && this.cierre.tandas) || []).length + 1; }

  /** FIR-06 (Mario, 24-oct): la banda de lo marcado la firma el Tesorero y quien envía no lo es → no queda firmada al enviarla. Sin `mis_roles_finanzas` (núcleo viejo) no se avisa. */
  get avisoSinTesorero(): boolean {
    const mis = this.cierre?.mis_roles_finanzas;
    return !!this.banda && this.banda.firmas?.[0] === 'TES' && Array.isArray(mis) && !mis.includes('TES');
  }

  enviarTanda = async (): Promise<void> => {
    if (!this.marcadas.size || this.enviando) return;
    this.enviando = true; this.error = '';
    try {
      const r = await this.liq.enviarTanda(this.dia, this.seleccion);
      await this.cargar();
      // FIR-06: después de enviar, si quedó esperando sin ninguna firma, se dice (cargar() limpia el aviso, por eso va después).
      const t = r?.tanda || r;
      if (t && t.state === 'POR_APROBAR' && !(t.firmas || []).length) this.aviso = `Tanda ${t.n} enviada. No quedó firmada: la firma ${this.firmantes(t.banda) || 'quien tenga la banda'}.`;
    }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo enviar la tanda.'; }
    finally { this.enviando = false; }
  };

  incluir = async (l: any): Promise<void> => {
    this.decidiendo = claveDe(l); this.error = '';
    try { await this.liq.decidir(this.dia, claveDe(l), 'INCLUIR', 'revisado por el tesorero'); await this.cargar(); }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo incluir.'; }
    finally { this.decidiendo = null; }
  };

  pasarAManana = async (l: any): Promise<void> => {
    if (this.motivoCodigoManana === 'OTRO' && !this.motivoManana.trim()) { this.error = 'Con «Otro», escribe el motivo.'; return; }
    this.decidiendo = claveDe(l); this.error = '';
    try { await this.liq.decidir(this.dia, claveDe(l), 'EXCLUIR', this.motivoManana.trim(), this.motivoCodigoManana); this.pasando = null; this.motivoManana = ''; this.motivoCodigoManana = 'SIN_APROBAR'; await this.cargar(); }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo pasar a mañana.'; }
    finally { this.decidiendo = null; }
  };

  // ── Cerrar el día con vista previa (Mario, 12-oct; CIE-07 a 09) ──
  previa: any = null;
  motivos: { [merchant: string]: { codigo: string; texto: string } } = {};
  asumo = false;
  asumoCuadre = false;
  motivoCodigoManana = 'SIN_APROBAR';
  readonly MOTIVOS_LISTA = [
    ['SIN_APROBAR', 'No se aprobó en el día'], ['CUENTA_NO_LISTA', 'Cuenta no lista'], ['NUEVO_POR_REVISAR', 'Comercio nuevo por revisar'],
    ['PEDIDO_COMERCIO', 'Pedido del comercio'], ['OTRO', 'Otro'],
  ];
  abrirPreviaCierre = async (): Promise<void> => {
    this.ocupado = true; this.error = ''; this.asumo = false; this.asumoCuadre = false;
    try {
      this.previa = await this.liq.cierrePrevio(this.dia);
      this.motivos = Object.fromEntries((this.previa.pasan || []).map((p: any) => [p.clave || p.merchant, { codigo: p.motivo_sugerido, texto: '' }]));
      setTimeout(() => document.getElementById('cerrar-dia')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo preparar el cierre.'; this.previa = null; }
    finally { this.ocupado = false; }
  };
  cerrarDia = async (): Promise<void> => {
    if (this.previa?.requiere_responsable && !this.asumo) { this.error = 'Para cerrar con algo sin liquidar, marca que quedas como responsable.'; return; }
    if (!puedeCerrarConCuadre(this.previa?.cuadre, this.asumoCuadre)) { this.error = 'Para cerrar con el cuadre sin cero, marca que asume la responsabilidad.'; return; }
    this.ocupado = true; this.error = '';
    try { await this.liq.cerrarDia(this.dia, { motivos: this.motivos, asumo: this.asumo, ...(this.previa?.cuadre?.requiere_responsable ? { asumo_cuadre: this.asumoCuadre } : {}) }); this.previa = null; this.asumo = false; this.asumoCuadre = false; await this.cargar(); }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo cerrar el día.'; }
    finally { this.ocupado = false; }
  };
  // ── El acta del día (ACT-09): PDF ejecutivo y Excel de detalle, desde la foto congelada al cerrar ──
  bajandoActa: 'pdf' | 'xlsx' | null = null;
  bajarActa = async (formato: 'pdf' | 'xlsx'): Promise<void> => {
    this.bajandoActa = formato; this.error = '';
    try {
      const blob = await this.liq.actaDelDia(this.dia, formato);
      guardarArchivo(blob, `acta-cierre-${this.dia.replace(/-/g, '')}.${formato}`);
    } catch (e: any) {
      // con responseType blob, el mensaje del núcleo viene dentro de un Blob
      let msg = ''; try { msg = JSON.parse(await e?.error?.text()).msg; } catch (x) { }
      this.error = msg || 'No se pudo descargar el acta.';
    } finally { this.bajandoActa = null; }
  };
  /** El nombre del comercio de la vista previa, desde las liquidaciones del cierre. */
  nombreDe = (code: string): string => (this.liquidaciones.find((l) => l.code === code) || {}).nombre || code;

  /** RSM-11: las tandas por firmar del cierre, para verlas en lectura. */
  get tandasVivas(): any[] { return ((this.cierre && this.cierre.tandas) || []).filter((t: any) => ['POR_APROBAR', 'FIRMA_1_DE_2'].includes(t.state)); }

  estadoTanda = (t: any): { texto: string; clase: string } => ({
    APROBADA: { texto: 'Aprobada', clase: 'kb-badge-exito' }, POR_APROBAR: { texto: 'Por aprobar', clase: 'kb-badge-alerta' },
    FIRMA_1_DE_2: { texto: 'Firma 1 de 2', clase: 'kb-badge-alerta' }, DEVUELTA: { texto: 'Devuelta', clase: 'kb-badge-neutro' },
  } as any)[t.state] || { texto: t.state, clase: 'kb-badge-neutro' };

  // ══ Por aprobar (4.6b; vistas 4 y 17) ═══════════════════════════════════════
  // La bandeja la arma el núcleo: solo las tandas cuya PRÓXIMA firma le toca a quien mira.
  bandeja: any[] = [];
  cargandoBandeja = false;
  firmando: string | null = null;
  devolviendo: string | null = null;
  motivoDevolver = '';
  avisoBandeja = '';

  claveTanda = (b: any): string => `${b.dia}-${b.tanda.n}`;

  // APR-10 / ALE-05 (Mario, 09-oct): quien firma audita cada pagaré antes de firmar y ve lo que conviene mirar.
  readonly ALERTA: Record<string, string> = { COMISION: 'Comisión distinta al contrato', MONTO_INUSUAL: 'Monto fuera de lo habitual', DUPLICADA: 'Venta duplicada', CUENTA_RECIENTE: 'Cuenta destino verificada hace poco' };
  detalleAbierto: string | null = null;
  bajandoTanda: string | null = null;
  verDetalle = (b: any, l: any): void => { const k = `${this.claveTanda(b)}-${l.code}`; this.detalleAbierto = this.detalleAbierto === k ? null : k; };
  detalleVisible = (b: any, l: any): boolean => this.detalleAbierto === `${this.claveTanda(b)}-${l.code}`;
  tasa = (p: any): string => p.tipo_comision === 'PERCENTAGE' ? `${String(p.tasa_comision).replace('.', ',')} %` : 'fija';

  excelTanda = async (b: any): Promise<void> => {
    this.bajandoTanda = this.claveTanda(b); this.error = '';
    try {
      const blob = await this.liq.excelTanda(b.dia, b.tanda.n);
      guardarArchivo(blob, `auditoria-tanda-${b.tanda.n}-${b.dia.replace(/-/g, '')}.xlsx`);
    } catch (e: any) { this.error = 'No se pudo descargar el Excel de la tanda.'; }
    finally { this.bajandoTanda = null; }
  };

  cargarBandeja = async (): Promise<void> => {
    this.cargandoBandeja = true; this.error = ''; this.cargando = false;
    try { const r = await this.liq.porAprobar(); this.bandeja = r?.tandas || []; this.esperan = r?.esperan_otra_firma || []; }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo leer la bandeja.'; }
    finally { this.cargandoBandeja = false; }
  };

  /** FIR-04 (Mario, 24-oct): las tandas vivas que no le tocan a quien mira, con el rol que falta y el porqué (un núcleo viejo no lo manda: []). */
  esperan: any[] = [];
  readonly PORQUE: any = {
    PREPARASTE: 'La preparaste tú: la firma otra persona',
    YA_FIRMASTE: 'Ya la firmaste',
    ROL_NO_ALCANZA: 'Tu rol no alcanza para esta banda',
    SIN_ROL_FINANZAS: 'Tu usuario no firma pagos (no tiene Tesorero, Gerente ni Director)',
  };
  porque = (k: string): string => this.PORQUE[k] || '';
  /** FIR-05: las que esperan un rol que nadie tiene (0 personas activas pueden dar esa firma). */
  get sinFirmante(): any[] { return this.esperan.filter((e: any) => e.firmantes_disponibles === 0); }
  rolDe = (k: string): string => this.ROL[k] || k;
  /** FIR-09 (Mario, 25-oct): la cinta dice si nadie tiene el rol o quién lo tiene y por qué no firma; FIR-10: Roles filtrado. */
  textoSinFirmante = (e: any): string => textoSF(this.rolDe(e.falta), e.tanda?.n, e.con_rol_sin_poder);
  etiquetaSinFirmante = etiquetaSF;
  rolesParams = rolesP;

  /**
   * FIR-08 (Mario, 25-oct): en «Por aprobar», el sello de estado de cada tanda viva y quién tiene que firmar.
   * `fi` = firmaron [{ rol, nombre, usuario }], `fs` = firmantes [{ nombre, usuario }] (un núcleo viejo no los manda: []).
   */
  selloFirma = (t: any, fi: any[] = []): { texto: string; clase: string } => {
    const n = (t?.firmas || []).length;
    if (!n) return { texto: 'Sin firmar', clase: 'kb-badge-alerta' };
    const quien = (fi || []).map((f: any) => f.nombre).join(', ');
    return { texto: `Firma ${n} de ${(t.banda?.firmas || []).length}${quien ? ` · firmó ${quien}` : ''}`, clase: 'kb-badge-alerta' };
  };
  /** FIR-08: «Nombre (usuario), …» con tope de 5 y «y N más». */
  listaFirmantes = (fs: any[] = []): string => {
    const lista = (fs || []).slice(0, 5).map((f: any) => (f.usuario ? `${f.nombre} (${f.usuario})` : f.nombre)).join(', ');
    const resto = (fs || []).length - 5;
    return resto > 0 ? `${lista} y ${resto} más` : lista;
  };
  /** FIR-08: el rol de la próxima firma de la tanda. */
  rolQueFalta = (t: any): string => this.rolDe(t?.banda?.firmas?.[(t.firmas || []).length]);

  /** BAN-09: con dos firmas, la que falta y de quién. */
  firmaQueFalta = (t: any): string => this.ROL[t.banda.firmas[(t.firmas || []).length]] || '';

  firmar = async (b: any): Promise<void> => {
    this.firmando = this.claveTanda(b); this.error = ''; this.avisoBandeja = '';
    try {
      const t = await this.liq.firmarTanda(b.dia, b.tanda.n);
      this.avisoBandeja = t.state === 'APROBADA' ? `Tanda ${t.n} del ${this.fecha(b.dia)} aprobada: nacieron sus cuentas por pagar.` : `Firmaste la tanda ${t.n}: falta la firma de ${this.firmaQueFalta(t)}.`;
      await this.cargarBandeja();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo firmar.'; }
    finally { this.firmando = null; }
  };

  devolver = async (b: any): Promise<void> => {
    if (!this.motivoDevolver.trim()) { this.error = 'Para devolverla escribe la observación.'; return; }
    this.firmando = this.claveTanda(b); this.error = ''; this.avisoBandeja = '';
    try {
      await this.liq.devolverTanda(b.dia, b.tanda.n, this.motivoDevolver.trim());
      this.avisoBandeja = `Tanda ${b.tanda.n} devuelta al tesorero con tu observación.`;
      this.devolviendo = null; this.motivoDevolver = '';
      await this.cargarBandeja();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo devolver.'; }
    finally { this.firmando = null; }
  };

  fecha = (iso: string): string => {
    const d = new Date(`${iso}T12:00:00Z`);
    return `${['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][d.getUTCDay()]} ${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  };

  private hoy(): string { return new Date(Date.now() - 4 * 3600e3).toISOString().slice(0, 10); }
}
